import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { Observable, catchError, tap } from 'rxjs';
import { AppConfig } from './app-config';
import { DOCUMENT } from '@angular/common';
import { PendoService } from '../services/pendo/pendo.service';
import { LicenseManager } from 'ag-grid-enterprise';

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private handler = inject(HttpBackend);
  private authClientConfig = inject(AuthClientConfig);
  private readonly document = inject<Document>(DOCUMENT);
  private readonly pendoService = inject(PendoService);

  config!: AppConfig;

  readonly configLocation = '/assets/app.config.json';
  readonly errorMessage =
    'Failed to load app.config.json file, does it exist? Is it in the correct format?';

  public load(): Observable<AppConfig> {
    const httpClient = new HttpClient(this.handler);
    return httpClient.get<AppConfig>(this.configLocation).pipe(
      tap((config) => {
        this.config = config;
        this.setupAuth0Config();
        this.loadPendo(config);
        this.loadAgGridLicense(config);
      }),
      catchError((error) => {
        throw error;
      })
    );
  }

  private setupAuth0Config(): void {
    if (!this.document.defaultView) {
      return;
    }

    // if not set, use the current origin as the redirect_uri
    const redirectUri =
      this.config.auth0Config.authorizationParams?.['redirect_uri'];
    if (!redirectUri) {
      this.config.auth0Config.authorizationParams = {
        ...this.config.auth0Config.authorizationParams,
        // eslint-disable-next-line camelcase
        redirect_uri: window.location.origin + '/callback',
      };
    }

    this.authClientConfig.set(this.config.auth0Config);
  }

  private async loadPendo(config: AppConfig) {
    await this.pendoService.loadScript(config);
  }

  private loadAgGridLicense(config: AppConfig) {
    if (config.agGridLicenseKey) {
      LicenseManager.setLicenseKey(config.agGridLicenseKey);
    } else {
      console.warn('No ag-Grid license key provided in app config.');
    }
  }
}
